import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, X, Loader2, ArrowUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { taskService } from '@/services/taskService'
import TaskTypeIcon from './TaskTypeIcon'
import TaskPicker from './TaskPicker'

// Jira-style hierarchy: Epic (level 1) → Task/Bug/Story (0) → Sub-task (-1).
const levelOf = (type) => type?.hierarchy_level ?? 0

const useTaskTypes = () => {
  const { data } = useQuery('task-types', () => taskService.getTaskTypes(), { staleTime: 5 * 60 * 1000 })
  return data?.data?.types || []
}

const invalidateProjectTasks = (queryClient, projectId) =>
  queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])

const statusDot = (category) => cn(
  'inline-block h-2 w-2 rounded-full shrink-0',
  category === 'done' ? 'bg-green-500' : category === 'in_progress' ? 'bg-blue-500' : 'bg-slate-400'
)

// Parent of this task: its epic (for Task/Bug/Story) or its task (for a Sub-task).
export const ParentField = ({ task, projectId, projectKey, onOpenTask, className, labelClassName = 'text-xs text-muted-foreground font-medium uppercase tracking-wide' }) => {
  const queryClient = useQueryClient()
  const level = levelOf(task.type)
  if (level >= 1) return null // epics have no parent

  const isSubtask = level === -1
  const label = isSubtask ? 'Parent task' : 'Epic'

  const setParent = async (parentId) => {
    try {
      await taskService.updateTask(projectId, task.id, { parent_id: parentId })
      invalidateProjectTasks(queryClient, projectId)
      toast.success(parentId ? `${label} set` : `${label} removed`)
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to update ${label.toLowerCase()}`)
    }
  }

  const allowed = (t) => t.id !== task.id && levelOf(t.type) === level + 1

  return (
    <div className={cn('space-y-1', className)}>
      <label className={labelClassName}>{label}</label>
      {task.parent ? (
        <div className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm">
          <TaskTypeIcon type={task.parent.type} />
          <button type="button" className="font-mono text-[11px] text-muted-foreground hover:text-primary" onClick={() => onOpenTask?.(task.parent)}>
            {task.parent.key || `${projectKey}-${task.parent.task_number}`}
          </button>
          <span className="truncate flex-1" title={task.parent.title}>{task.parent.title}</span>
          <TaskPicker projectId={projectId} projectKey={projectKey} filter={allowed} onSelect={(t) => setParent(t.id)}>
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground">Change</button>
          </TaskPicker>
          {!isSubtask && (
            <button type="button" className="text-muted-foreground hover:text-destructive" title="Remove from epic" onClick={() => setParent(null)}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        <TaskPicker projectId={projectId} projectKey={projectKey} filter={allowed} onSelect={(t) => setParent(t.id)}>
          <Button variant="outline" size="sm" className="h-8 w-full justify-start font-normal text-sm text-muted-foreground">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> {isSubtask ? 'Choose parent task' : 'Add to epic'}
          </Button>
        </TaskPicker>
      )}
    </div>
  )
}

// Children: sub-tasks of a task, or the tasks of an epic — with progress and quick add.
export const ChildItems = ({ task, projectId, projectKey, onOpenTask }) => {
  const queryClient = useQueryClient()
  const types = useTaskTypes()
  const [title, setTitle] = useState('')
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const level = levelOf(task.type)
  if (level < 0) return null // sub-tasks have no children

  const isEpic = level === 1
  const childType = types.filter(t => levelOf(t) === level - 1)
    .sort((a, b) => (/^(task|sub-task)$/i.test(b.name) ? 1 : 0) - (/^(task|sub-task)$/i.test(a.name) ? 1 : 0))[0]
  const children = task.children || []
  const progress = task.childProgress || { total: children.length, done: children.filter(c => c.status?.category === 'done').length }
  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!title.trim() || !childType) return
    setSaving(true)
    try {
      await taskService.createTask(projectId, { title: title.trim(), type_id: childType.id, parent_id: task.id })
      setTitle('')
      invalidateProjectTasks(queryClient, projectId)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">
          {isEpic ? 'Tasks in this epic' : 'Sub-tasks'}
          {progress.total > 0 && <span className="text-muted-foreground font-normal ml-1">({progress.done}/{progress.total} done)</span>}
        </h3>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding(v => !v)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> {isEpic ? 'Add task' : 'Add sub-task'}
        </Button>
      </div>
      {progress.total > 0 && <Progress value={percent} className="h-1.5" />}
      {children.length > 0 && (
        <div className="rounded-md border divide-y">
          {children.map(child => (
            <button
              key={child.id}
              type="button"
              onClick={() => onOpenTask?.(child)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-muted/50"
            >
              <TaskTypeIcon type={child.type} />
              <span className="font-mono text-[11px] text-muted-foreground shrink-0">{child.key || `${projectKey}-${child.task_number}`}</span>
              <span className={cn('truncate flex-1', child.status?.category === 'done' && 'line-through text-muted-foreground')}>{child.title}</span>
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                <span className={statusDot(child.status?.category)} /> {child.status?.name}
              </span>
              <ArrowUpRight className="h-3 w-3 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
      {adding && (
        <form onSubmit={handleAdd} className="flex items-center gap-2">
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isEpic ? 'What needs to be done in this epic?' : 'Sub-task title'}
            className="h-8 text-sm"
            onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setTitle('') } }}
          />
          <Button type="submit" size="sm" className="h-8" disabled={!title.trim() || saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
          </Button>
        </form>
      )}
      {!children.length && !adding && (
        <p className="text-xs text-muted-foreground">{isEpic ? 'No tasks in this epic yet.' : 'No sub-tasks yet.'}</p>
      )}
    </div>
  )
}
